import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { DESKTOP_BREAKPOINT } from "../../components/AppShell/styles";
import { SubHeader } from "../../components/SubHeader";
import { Card, Container, Section } from "../Settings/styles";
import { usePayments } from "./state";

const prefix = "pages.charges.terms";
const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;
const GREEN = "#57f28b";

// The marks of the terms' texts: <b> what really matters, <u> what's easy to miss, <i> a side note, <help> the
// link to Ajuda
const MARKS = {
  b: <strong />,
  u: <u />,
  i: <em />,
  help: <Link to="/help" />,
};

const Text = styled.p`
  margin: 0;
  color: var(--color-neutral-150);
  font-family: Inter, sans-serif;
  font-size: 0.9375rem;
  line-height: 1.5rem;
  text-align: justify;

  /* a narrow column justified opens gaps between the words */
  @media ${MOBILE} {
    text-align: left;
  }

  /* the key terms, values and deadlines: white is highlight enough, no bold */
  strong {
    color: var(--color-neutral-white);
    font-weight: inherit;
  }

  /* a link: white and underlined */
  a {
    color: var(--color-neutral-white);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  /* what's easy to miss */
  u {
    color: var(--color-neutral-white);
    text-decoration-color: currentColor;
    text-underline-offset: 3px;
  }

  /* side notes (the app's reset takes the italics off <em>) */
  em {
    color: var(--color-neutral-150);
    font-style: italic;
  }
`;

/* An enumeration of a clause, item by item */
const Items = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  padding-left: 1.25rem;
  margin: 0;
  list-style: disc;

  li::marker {
    color: ${GREEN};
  }
`;

/* When the terms were last changed, opening the card */
const Updated = styled.span`
  color: var(--color-neutral-150);
  font-family: Inter, sans-serif;
  font-size: 0.8125rem;
`;

/* What the terms cover, smaller, opening the card */
const Intro = styled(Text)`
  padding-bottom: 1.25rem;
  font-size: 0.8125rem;
  line-height: 1.25rem;
  border-bottom: 1px solid var(--color-neutral-base);
`;

/* One clause: its numbered title and its paragraphs */
const Clause = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;

  & + & {
    padding-top: 1.25rem;
    border-top: 1px solid var(--color-neutral-base);
  }

  h2 {
    margin: 0;
    color: var(--color-neutral-white);
    font-family: Inter, sans-serif;
    font-size: 1rem;
    font-weight: 600;
  }
`;

// A clause's paragraph, or a list of items
type ClauseText = { title: string; paragraphs: (string | { items: string[] })[] };

/** The terms of Cobranças, as a page of the editor: what sending the registration to receive accepts. */
const ChargesTermsPage = () => {
  const { t } = useTranslation();
  const sections = t(`${prefix}.sections`, { returnObjects: true }) as ClauseText[];
  // the card's days until the money is available, as the API has them (30 until known)
  const { payments } = usePayments();
  const values = { days: typeof payments === "object" ? payments.cardReleaseDays : 30 };

  return (
    <Container>
      <SubHeader title={t(`${prefix}.title`)} crumbs={[{ label: t("pages.charges.title"), to: "/cobrancas" }]} />

      <Section>
        <Card>
          <Updated>{t(`${prefix}.updated`)}</Updated>
          <Intro>{t(`${prefix}.intro`)}</Intro>
          {sections.map((section, index) => (
            <Clause key={section.title} aria-labelledby={`charges-terms-${index}`}>
              <h2 id={`charges-terms-${index}`}>{section.title}</h2>
              {section.paragraphs.map((paragraph, at) => {
                const key = `${prefix}.sections.${index}.paragraphs.${at}`;
                return typeof paragraph === "string" ? (
                  <Text key={key}>
                    <Trans i18nKey={key} components={MARKS} values={values} />
                  </Text>
                ) : (
                  <Text as={Items} key={key}>
                    {paragraph.items.map((_, item) => (
                      <li key={item}>
                        <Trans i18nKey={`${key}.items.${item}`} components={MARKS} values={values} />
                      </li>
                    ))}
                  </Text>
                );
              })}
            </Clause>
          ))}
        </Card>
      </Section>
    </Container>
  );
};

export default ChargesTermsPage;
